import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  DownloadIcon,
  EditIcon,
  ExternalLinkIcon,
  AlertTriangleIcon,
  FileTextIcon,
  ClockIcon,
  UsersIcon,
  Building2Icon,
} from "lucide-react";
import type { TemplateDetailDTO, TemplateVersionDTO } from "../../domain/contracts";
import { formatDateLocal } from "@/shared/lib/date-time";
import { resolveImageSrc } from "@/shared/ui/AppImage";
import { TemplateVersionHistory } from "./TemplateVersionHistory";

type TemplateDetailViewProps = {
  template: TemplateDetailDTO;
  versions?: TemplateVersionDTO[];
  onEdit?: () => void;
  onDownload?: () => void;
  onPreview?: () => void;
  canEdit?: boolean;
};

const statusLabels: Record<string, string> = {
  active: "ໃຊ້ງານ",
  deprecated: "ເກົ່າ",
  archived: "ບໍ່ໃຊ້ງານ",
};

const statusVariants: Record<string, "default" | "secondary" | "destructive"> = {
  active: "default",
  deprecated: "secondary",
  archived: "destructive",
};

const formatFileSize = (bytes: number | null): string => {
  if (!bytes) return "-";
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${Math.round(mb * 10) / 10} MB`;
};

export function TemplateDetailView({
  template,
  versions,
  onEdit,
  onDownload,
  onPreview,
  canEdit,
}: TemplateDetailViewProps) {
  const nav = useNavigate();

  const handleDownload = () => {
    if (template.currentVersionFileUrl) {
      const fileUrl = resolveImageSrc(template.currentVersionFileUrl);
      if (fileUrl) {
        const link = document.createElement("a");
        link.href = fileUrl;
        link.download = template.currentVersionFileName || "template";
        link.click();
      }
    } else if (onDownload) {
      onDownload();
    }
  };

  const handlePreview = () => {
    if (template.currentVersionFileUrl) {
      const fileUrl = resolveImageSrc(template.currentVersionFileUrl);
      if (fileUrl) window.open(fileUrl, "_blank", "noopener,noreferrer");
    } else if (onPreview) {
      onPreview();
    }
  };

  const isDeprecated = template.status === "deprecated";
  const hasCurrentVersion = !!template.currentVersionFileUrl;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {/* Header with actions */}
      <div className="flex items-start justify-between" data-tourid="detail-header">
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="font-bold text-3xl">{template.name}</h1>
          </div>
          {/* <div className="mt-2 space-y-1">
            {template.categoryName && (
              <p className="text-muted-foreground">
                <span className="font-medium">ປະເພດ:</span> {template.categoryName}
                {template.categoryCode && ` (${template.categoryCode})`}
              </p>
            )}
            {template.departments && template.departments.length > 0 && (
              <div className="flex items-center gap-2">
                <Building2Icon className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground text-sm">
                  {template.departments.map((d) => d.departmentName).join(", ")}
                </span>
              </div>
            )}
            {(template.description || template.usageNotes) && (
              <p className="text-muted-foreground text-sm line-clamp-2">
                {template.description || template.usageNotes}
              </p>
            )}
            {(template.downloadCount > 0 || template.lastUsedAt) && (
              <div className="flex items-center gap-4 text-muted-foreground text-xs">
                {template.downloadCount > 0 && (
                  <div className="flex items-center gap-1">
                    <DownloadIcon className="h-3 w-3" />
                    <span>ດາວໂຫຼດ {template.downloadCount} ເທື່ອ</span>
                  </div>
                )}
                {template.lastUsedAt && (
                  <div className="flex items-center gap-1">
                    <ClockIcon className="h-3 w-3" />
                    <span>ໃຊ້ລ່າສຸດ: {formatDateLocal(template.lastUsedAt)}</span>
                  </div>
                )}
              </div>
            )}
          </div> */}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => nav({ to: "/app/template" })}>
            <ArrowLeftIcon className="h-4 w-4" />
            ກັບໄປລາຍການ
          </Button>
          {hasCurrentVersion && (
            <>
              <Button variant="outline" onClick={handlePreview}>
                <ExternalLinkIcon className="h-4 w-4" />
                ເບີ່ງຕົວຢ່າງ
              </Button>
              <Button variant="outline" onClick={handleDownload}>
                <DownloadIcon className="h-4 w-4" />
                ດາວໂຫຼດຮ່າງເອກະສານ
              </Button>
            </>
          )}
          {canEdit && onEdit && (
            <Button onClick={onEdit}>
              <EditIcon className="h-4 w-4" />
              ແກ້ໄຂ
            </Button>
          )}
        </div>
      </div>

      {/* Warning for deprecated template */}
      {isDeprecated && (
        <Card className="border-amber-500 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertTriangleIcon className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-900">ເຕືອນ: ຮ່າງເອກະສານເກົ່າ</h3>
                <p className="mt-1 text-amber-800 text-sm">
                  ຮ່າງເອກະສານນີ້ຖືກສະແດງວ່າເປັນເວີຊັນເກົ່າແລ້ວ. ກະລຸນາກວດເບິ່ງເວີຊັນໃໝ່ກ່ອນການໃຊ້ງານ.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section 1: Basic Information */}
      <Card data-tourid="detail-basic-info">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileTextIcon className="h-5 w-5" />
            ຂໍ້ມູນພື້ນຖານ
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h3 className="mb-1 font-medium text-sm text-muted-foreground">
              ຊື່ຮ່າງເອກະສານ
            </h3>
            <p className="font-semibold text-lg">{template.name}</p>
          </div>

          {template.description && (
            <div>
              <h3 className="mb-1 font-medium text-sm text-muted-foreground">
                ໃຊ້ກັບວຽກອັນໃດ (ການອະທິບາຍ)
              </h3>
              <p className="whitespace-pre-wrap text-sm">{template.description}</p>
            </div>
          )}

          {template.categoryName && (
            <div>
              <h3 className="mb-1 font-medium text-sm text-muted-foreground">
                ປະເພດຮ່າງເອກະສານ
              </h3>
              <Badge variant="outline">{template.categoryName}</Badge>
            </div>
          )}

          {template.departments && template.departments.length > 0 && (
            <div>
              <h3 className="mb-2 font-medium text-sm text-muted-foreground flex items-center gap-1">
                <Building2Icon className="h-4 w-4" />
                ພະແນກທີ່ໃຊ້
              </h3>
              <div className="flex flex-wrap gap-2">
                {template.departments.map((dept) => (
                  <Badge
                    key={dept.departmentId}
                    variant={dept.isPrimary ? "default" : "outline"}
                  >
                    {dept.departmentName}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {template.usageNotes && (
            <div>
              <h3 className="mb-1 font-medium text-sm text-muted-foreground">
                ຫມາຍເຫດການນຳໃຊ້
              </h3>
              <div className="rounded-lg bg-muted p-3">
                <p className="whitespace-pre-wrap text-sm">{template.usageNotes}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section 2: Current Version Info */}
      {hasCurrentVersion && (
        <Card data-tourid="detail-current-version">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ClockIcon className="h-5 w-5" />
              ເວີຊັນປັດຈຸບັນ
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <h3 className="mb-1 font-medium text-sm text-muted-foreground">
                  ເວີຊັນ
                </h3>
                <Badge variant="outline" className="text-base">
                  {template.currentVersion || "-"}
                </Badge>
              </div>
              {template.currentVersionFileSize && (
                <div>
                  <h3 className="mb-1 font-medium text-sm text-muted-foreground">
                    ຂະໜາດຟາຍ
                  </h3>
                  <p className="text-sm">{formatFileSize(template.currentVersionFileSize)}</p>
                </div>
              )}
              {template.currentVersionMimeType && (
                <div>
                  <h3 className="mb-1 font-medium text-sm text-muted-foreground">
                    ປະເພດຟາຍ
                  </h3>
                  <p className="text-sm">{template.currentVersionMimeType}</p>
                </div>
              )}
              {template.lastUsedAt && (
                <div>
                  <h3 className="mb-1 font-medium text-sm text-muted-foreground">
                    ໃຊ້ງານລ່າສຸດ
                  </h3>
                  <p className="text-sm">
                    {formatDateLocal(template.lastUsedAt)}
                  </p>
                </div>
              )}
            </div>
            <div className="pt-2">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handlePreview}>
                  <ExternalLinkIcon className="h-4 w-4" />
                  ເບີ່ງຕົວຢ່າງ
                </Button>
                <Button size="sm" onClick={handleDownload}>
                  <DownloadIcon className="h-4 w-4" />
                  ດາວໂຫຼດ
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section 3: Version History */}
      {versions && versions.length > 0 && (
        <Card data-tourid="detail-version-history">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UsersIcon className="h-5 w-5" />
              ປະຫວັດການແກ້ໄຂເວີຊັນ
            </CardTitle>
          </CardHeader>
          <CardContent>
            <TemplateVersionHistory
              templateId={template.id}
              versions={versions}
              currentVersionId={template.currentVersionId}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
